<template>
  <div
    v-click-outside="() => (isOpen = false)"
    class="dropdown"
    :class="classes"
  >
    <Button
      class="dropdown__button"
      :theme="theme"
      :form="form"
      width="full"
      size="medium"
      @click="toggleDropDown"
    >
      <template v-if="title">{{ title }} - </template>{{ selectedOptionLabel }}
      <i class="icon" :class="arrowIcon" />
    </Button>
    <ul v-if="isOpen" class="dropdown__panel">
      <li v-for="option in options" :key="option.value" class="dropdown__item">
        <button
          type="button"
          class="dropdown__item-button"
          :disabled="option.disabled"
          @click="handleOptionClick(option)"
        >
          <span class="label">{{ option.label }}</span>
          <span class="status">{{ option.status }}</span>
        </button>
      </li>
    </ul>
  </div>
</template>

<script>
import Button from "@/components/ButtonV2.vue";

export default {
  name: "DropDown",
  components: {
    Button,
  },
  props: {
    direction: {
      type: String,
      default: "down",
      validator: (value) => {
        return ["up", "down"].includes(value);
      },
    },
    theme: {
      type: String,
      default: "white",
    },
    form: {
      type: String,
      default: "outline",
    },
    title: {
      type: String,
      default: null,
    },
    options: {
      type: Array,
      required: true,
    },
    selected: {
      type: String,
      default: null,
    },
    defaultLabel: {
      type: String,
      default: "Select",
    },
    transparent: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["changed"],
  data() {
    return {
      isOpen: false,
      selectedOption: null,
    };
  },
  computed: {
    arrowIcon() {
      return this.isOpen ? "icon-arrow-up" : "icon-arrow-down";
    },
    selectedOptionLabel() {
      return this.selectedOption
        ? this.selectedOption.label
        : this.defaultLabel;
    },
    classes() {
      return {
        "dropdown--up": this.direction === "up",
        "dropdown--error": this.error,
      };
    },
  },
  mounted() {
    if (this.selected) {
      this.selectedOption = this.options.find(
        (option) => option.value === this.selected
      );
    }
  },
  methods: {
    handleOptionClick(option) {
      this.selectedOption = option;
      this.$emit("changed", option.value);
      this.isOpen = false;
    },
    toggleDropDown() {
      this.isOpen = !this.isOpen;
    },
    doStuff() {
      this.isOpen = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/components/form.scss";

.dropdown {
  position: relative;

  &--error {
    :deep(.button) {
      border-color: $color-red;
      color: $color-red;
    }
  }

  &__item-button {
    background: transparent;
    border: none;
    padding: 14px;
    display: block;
    width: 100%;
    z-index: 1;
    text-align: left;
    cursor: pointer;
    display: flex;
    justify-content: space-between;

    &:disabled {
      cursor: not-allowed;
      color: rgba($color-grey-dark, 0.2);
    }

    &:hover {
      background-color: $color-grey-light;
    }

    .label {
      @include utility-label(14px);
    }

    .status {
      @include utility-label(14px);
      font-weight: 400;
      text-transform: none;
    }
  }

  &__panel {
    position: absolute;
    left: 0;
    right: 0;
    background-color: $color-white;
    z-index: 2;
    box-shadow: 0 0 0 0.5px $color-grey-dark;

    .dropdown--up & {
      bottom: 100%;
    }
  }

  :deep() {
    .button .text {
      text-align: left;
      display: block;
    }
  }

  &__button {
    position: relative;
    text-align: left;
    padding: 9px 12px;
  }
}

.icon {
  font-size: 1em;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
}
</style>
